// Read-only aggregate contract v1. Shared by Edge and PWA; no persistence authority.
export interface MissingRange { from: string; to: string; reason: string }
export interface AggregateEvidence {
  effective_event_ids: string[]
  interval_from: string
  interval_to: string
  computed_from: 'events' | 'projections' | 'shadows' | 'none'
  missing_ranges: MissingRange[]
  // Null means no independently known projection timestamp, never the request time.
  last_projection_at: string | null
}
export type Aggregate =
  | { status: 'known'; number: number; unit: string; label: string; evidence: AggregateEvidence }
  | { status: 'unknown'; reason: string }
export interface AggregateEvent {
  id: string; family_id: string; person_id: string | null; event_type: string
  occurred_at: string; server_received_at: string; server_sequence: number | string
}
// Exact constrained authorship, including spelled-out quantities. A digit regex
// cannot enforce INV-14. Structured totals, not generated prose, carry numbers.
export const AGGREGATE_NARRATIONS = [
  'These are recorded observations, not a complete account of care. Open each total to inspect its records and interval.',
  'I cannot verify that interpretation from the available record. Please inspect the contributing observations.',
] as const
export function validateAggregateNarration(text: string): string | null {
  return AGGREGATE_NARRATIONS.find((allowed) => allowed === text.trim()) ?? null
}
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const timestamp = (v: unknown): v is string => typeof v === 'string'
  && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?(?:Z|[+-]\d\d:\d\d)$/.test(v) && Number.isFinite(Date.parse(v))
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
export function validEvidence(v: unknown): v is AggregateEvidence {
  if (!object(v) || !Array.isArray(v.effective_event_ids) || v.effective_event_ids.length > 500
    || !v.effective_event_ids.every((id) => typeof id === 'string' && UUID.test(id))
    || new Set(v.effective_event_ids).size !== v.effective_event_ids.length
    || !timestamp(v.interval_from) || !timestamp(v.interval_to) || Date.parse(v.interval_from) > Date.parse(v.interval_to)
    || !['events', 'projections', 'shadows', 'none'].includes(String(v.computed_from))
    || !(v.last_projection_at === null || timestamp(v.last_projection_at))
    || (typeof v.last_projection_at === 'string' && Date.parse(v.last_projection_at) > Date.parse(v.interval_to))
    || !Array.isArray(v.missing_ranges) || v.missing_ranges.length > 500) return false
  const from = Date.parse(v.interval_from), to = Date.parse(v.interval_to)
  return v.missing_ranges.every((gap) => object(gap) && timestamp(gap.from) && timestamp(gap.to)
    && Date.parse(gap.from) >= from && Date.parse(gap.to) <= to && Date.parse(gap.from) <= Date.parse(gap.to)
    && typeof gap.reason === 'string' && gap.reason.length > 0 && gap.reason.length <= 500)
}
export function validAggregate(v: unknown): v is Aggregate {
  if (!object(v)) return false
  if (v.status === 'unknown') return typeof v.reason === 'string' && !!v.reason && !('number' in v)
  return v.status === 'known' && typeof v.number === 'number' && Number.isFinite(v.number) && v.number >= 0
    && typeof v.unit === 'string' && typeof v.label === 'string' && validEvidence(v.evidence)
    && v.evidence.computed_from !== 'none'
    && (v.number === 0 || v.evidence.effective_event_ids.length > 0)
}
export const unknownAggregate = (reason: string): Aggregate => ({ status: 'unknown', reason })

/** Defense in depth after the SECURITY DEFINER RPC: exact set, live RLS, family,
 * subject and observation fence. A missing/truncated/foreign row is not a zero. */
export function verifyEvidenceScope(evidence: AggregateEvidence, rows: AggregateEvent[], familyId: string, personId: string): boolean {
  return validEvidence(evidence) && evidence.computed_from === 'events'
    && rows.length === evidence.effective_event_ids.length && new Set(rows.map((r) => r.id)).size === rows.length
    && rows.every((r) => evidence.effective_event_ids.includes(r.id) && r.family_id === familyId && r.person_id === personId
      && timestamp(r.occurred_at) && timestamp(r.server_received_at)
      && Date.parse(r.occurred_at) <= Date.parse(evidence.interval_to)
      && Date.parse(r.server_received_at) <= Date.parse(evidence.interval_to))
}
export function aggregateFromEvents(kind: 'feeds' | 'diapers' | 'sleep', evidence: AggregateEvidence, rows: AggregateEvent[], familyId: string, personId: string): Aggregate {
  if (!verifyEvidenceScope(evidence, rows, familyId, personId)) return unknownAggregate('Evidence unavailable, truncated or outside the authorized scope.')
  const from = Date.parse(evidence.interval_from), to = Date.parse(evidence.interval_to)
  if (kind !== 'sleep') {
    const prefix = kind === 'feeds' ? 'early_care.feeding' : 'early_care.diaper'
    if (rows.some((r) => ![`${prefix}.recorded`, `${prefix}.corrected`].includes(r.event_type) || Date.parse(r.occurred_at) < from))
      return unknownAggregate('Evidence does not match this metric.')
    return { status: 'known', number: rows.length, unit: kind, label: 'logged in this interval', evidence }
  }
  const sleep = [...rows].sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at)
    || (BigInt(a.server_sequence) < BigInt(b.server_sequence) ? -1 : 1))
  if (sleep.length % 2) return unknownAggregate('An unpaired sleep record prevents a verified duration.')
  let minutes = 0
  for (let i = 0; i < sleep.length; i += 2) {
    const a = sleep[i], b = sleep[i + 1], start = Date.parse(a.occurred_at), end = Date.parse(b.occurred_at)
    if (a.event_type !== 'early_care.sleep.started' || b.event_type !== 'early_care.sleep.ended' || end <= start || end < from)
      return unknownAggregate('Sleep records are ambiguous or incomplete.')
    minutes += (Math.min(end, to) - Math.max(start, from)) / 60_000
  }
  return { status: 'known', number: Math.round(minutes), unit: 'minutes of sleep', label: 'logged in this interval', evidence }
}
